import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState, User } from '../types';
import { authService, VerificationRequired, EmailVerificationRequired, AccountBlocked, LoginResult, RegisterResult } from '../services/auth';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyCode: (verificationId: string, code: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  register: (name: string, email: string, phone: string, password: string) => Promise<RegisterResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function isVerificationRequired(result: LoginResult): result is VerificationRequired {
  return 'requiresVerification' in result && result.requiresVerification === true;
}

function isEmailVerificationRequired(result: LoginResult): result is EmailVerificationRequired {
  return 'requiresEmailVerification' in result && result.requiresEmailVerification === true;
}

function isAccountBlocked(result: LoginResult): result is AccountBlocked {
  return 'isBlocked' in result && result.isBlocked === true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const user = await authService.getMe();
          setState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          localStorage.removeItem('token');
          setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await authService.login(email, password);

    if (isVerificationRequired(result)) {
      // Return the verification required response - caller handles navigation
      return result;
    }

    if (isEmailVerificationRequired(result)) {
      // Return the email verification required response - caller handles navigation
      return result;
    }

    if (isAccountBlocked(result)) {
      // Return the blocked response - caller handles display
      return result;
    }

    // Normal login success
    const { user, token } = result as { user: User; token: string };
    localStorage.setItem('token', token);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
    return result;
  };

  const verifyCode = async (verificationId: string, code: string) => {
    const { user, token, deviceToken } = await authService.verifyCode(verificationId, code);
    localStorage.setItem('token', token);
    localStorage.setItem('deviceToken', deviceToken);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const register = async (name: string, email: string, phone: string, password: string): Promise<RegisterResult> => {
    const result = await authService.register(name, email, phone, password);
    // Registration now requires email verification, no immediate login
    return result;
  };

  const verifyEmail = async (token: string) => {
    const { user, token: authToken, deviceToken } = await authService.verifyEmail(token);
    localStorage.setItem('token', authToken);
    localStorage.setItem('deviceToken', deviceToken);
    setState({
      user,
      token: authToken,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getMe();
      setState(prev => ({ ...prev, user }));
    } catch {
      // ignore
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, verifyCode, verifyEmail, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
